import 'yet-another-react-lightbox/styles.css';

import React, { SyntheticEvent, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { Box, Stack, styled, useTheme } from '@mui/material';
import { getFileType, getFileUrl } from 'src/utils/fileUploadUtils';

import Iconify from '../iconify';
import Lightbox from '../lightbox/Lightbox';
import FileUpload from '../upload/FileUpload';

interface IRHFUploadImageProps {
  name: string;
  files: Array<File | string>; // 기존 파일 (URL 또는 File)
  maxFiles?: number; // 최대 파일 수
  accept?: Record<string, any>; // 허용 파일 타입
  onChange: (files: Array<File | string>) => void; // 파일 변경 콜백
}

const RHFUploadDynamic: React.FC<IRHFUploadImageProps> = ({
  name,
  files = [],
  maxFiles = Infinity,
  accept = {
    'image/jpeg': [],
    'image/jpg': [],
    'image/png': [],
  },
  onChange,
  ...other
}) => {
  const { control } = useFormContext();
  const theme = useTheme();
  // 이미지 선택 시 확대
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // 배열인지 확인 후 기본값 설정
  const isFileSafe = Array.isArray(files);

  // 드래그 앤 드롭 순서 변경 처리
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const reorderedFiles = Array.from(files);
    const [movedFile] = reorderedFiles.splice(result.source.index, 1);

    // 비디오 파일은 드래그 앤 드롭 제외
    if (movedFile instanceof File && movedFile.type.startsWith('video')) {
      return;
    }

    reorderedFiles.splice(result.destination.index, 0, movedFile);
    onChange(reorderedFiles);
  };

  // 파일 제거
  const handleRemoveFile = (e: SyntheticEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    const newFiles = files.filter((_, i) => i !== index);
    onChange(newFiles);
  };

  // 파일 추가
  const handleDropFiles = (newFiles: File[]) => {
    const combinedFiles = [...files, ...newFiles];
    if (combinedFiles.length > maxFiles) {
      alert(`업로드 가능한 파일 수는 ${maxFiles}개 입니다.`);
      return;
    }
    onChange(combinedFiles);
  };

  // 이미지 프리뷰 렌더링
  const renderFileMedia = (file: File | string) => {
    const fileTypeData = getFileType(file, accept);
    const { type, preview } = fileTypeData; // getFileType에서 반환된 타입과 미리보기 정보
    const fileURL = getFileUrl(file); // 파일 URL 생성 함수
    const fileName = typeof file === 'string' ? file.split('/').pop() : file.name;

    // 이미지 파일 처리
    if (type === 'image') {
      return (
        <FileContainer>
          <FileIconImage src={fileURL} />
          <FileNm>{fileName}</FileNm>
        </FileContainer>
      );
    }

    // 비디오 파일 처리
    if (type === 'video') {
      return (
        <FileContainer>
          <FileIconVideo>
            <source src={fileURL} type="video/mp4" />
          </FileIconVideo>
          <FileNm>{fileName}</FileNm>
        </FileContainer>
      );
    }

    // 기타 파일 처리
    const supportedFileTypes = ['pdf', 'excel', 'ppt', 'word', 'zip', 'Data File'];
    if (supportedFileTypes.includes(type)) {
      return (
        <FileContainer>
          <FileIconImage src={preview} alt={type} />
          <FileNm>{fileName}</FileNm>
        </FileContainer>
      );
    }

    // 지원하지 않는 파일 처리
    return (
      <div style={{ textAlign: 'center', color: theme.palette.text.secondary }}>
        <FileIconImage src={preview} alt="Unsupported" />
        <FileNm>{fileName}</FileNm>
      </div>
    );
  };

  const handleClickOpen = (index: number) => {
    setCurrentIndex(index);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        return (
          <Container {...other}>
            {isFileSafe && (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="uploaded-files" direction="vertical">
                  {(provided) => (
                    <DraggableContainer ref={provided.innerRef} {...provided.droppableProps}>
                      {files.map((file, index) => (
                        <Draggable key={index} draggableId={`file-${index}`} index={index}>
                          {(provided) => (
                            <MediaBox
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => handleClickOpen(index)}
                            >
                              {renderFileMedia(file)}
                              <DeleteButton onClick={(e) => handleRemoveFile(e, index)}>
                                <Iconify
                                  icon="eva:close-fill"
                                  width={20}
                                  color={theme.palette.grey[500]}
                                />
                              </DeleteButton>
                            </MediaBox>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </DraggableContainer>
                  )}
                </Droppable>
              </DragDropContext>
            )}
            {files.length < maxFiles && (
              <div>
                <FileUpload
                  files={[]}
                  showPreview
                  showFileIcon={false}
                  onDrop={handleDropFiles}
                  accept={accept}
                />
              </div>
            )}

            <Lightbox
              open={open}
              close={handleClose}
              index={currentIndex}
              slides={files.map((file) => {
                const fileURL = getFileUrl(file);
                return { src: fileURL };
              })}
            />
          </Container>
        );
      }}
    />
  );
};

export default RHFUploadDynamic;

const Container = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
  overflowX: 'auto',
}));

const DraggableContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const MediaBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.grey[500]}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1),
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const DeleteButton = styled('span')(({ theme }) => ({
  zIndex: 1,
  width: 30,
  height: 30,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
}));

// 일반
const FileContainer = styled('div')(({ theme }) => ({
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1),
  alignItems: 'center',
}));

const FileNm = styled('span')(({ theme }) => ({
  fontSize: theme.typography.subtitle2.fontSize,
}));

const FileIconImage = styled('img')(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: 8,
}));
const FileIconVideo = styled('video')(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: 8,
}));
